import i18n from '../i18n';

// Cache for reverse translation map (zone name in any language -> English)
let reverseTranslationCache = null;

// Function to clear the cache (useful when language changes)
export const clearTranslationCache = () => {
  reverseTranslationCache = null;
};

/**
 * Builds a reverse translation map for efficient lookup
 * Maps translated zone names in any language to their English equivalents
 */
const buildReverseTranslationMap = () => {
  if (reverseTranslationCache) {
    return reverseTranslationCache;
  }

  const map = new Map();
  const languages = ['en', 'kn', 'ta', 'ml', 'hi', 'mr', 'te'];
  const englishResources = i18n.getResourceBundle('en', 'translation');
  const englishBodyParts = englishResources?.bodyParts || {};

  // Build reverse map: translated value (normalized) -> English value
  for (const lang of languages) {
    try {
      const resources = i18n.getResourceBundle(lang, 'translation');
      const langBodyParts = resources?.bodyParts || {};
      
      for (const [key, value] of Object.entries(langBodyParts)) {
        if (value && typeof value === 'string' && englishBodyParts[key]) {
          // Normalize the translated value for lookup
          const normalize = (str) => str.toLowerCase().trim().replace(/\s+/g, ' ');
          const normalized = normalize(value);
          const englishValue = englishBodyParts[key];
          
          // Map normalized translated value to English value
          if (!map.has(normalized)) {
            map.set(normalized, englishValue);
          }
          // Also map the original (non-normalized) value
          const originalTrimmed = value.trim();
          const originalNormalized = normalize(originalTrimmed);
          if (originalNormalized !== normalized && !map.has(originalNormalized)) {
            map.set(originalNormalized, englishValue);
          }
          // Also map the exact original value (case-insensitive)
          if (!map.has(originalTrimmed.toLowerCase())) {
            map.set(originalTrimmed.toLowerCase(), englishValue);
          }
        }
      }
    } catch (e) {
      // If resource bundle doesn't exist, skip it
      continue;
    }
  }

  reverseTranslationCache = map;
  return map;
};

/**
 * Translates a zone name to the current language
 * @param {string} zoneName - The zone name (could be in any language)
 * @param {Function} t - Optional translation function from useTranslation hook (for reactivity)
 * @returns {string} - Translated zone name or original if translation not found
 */
export const translateZoneName = (zoneName, t = null) => {
  if (!zoneName || typeof zoneName !== 'string') {
    return zoneName;
  }

  // Normalize the zone name (lowercase, trim, remove extra spaces)
  const normalize = (str) => str.toLowerCase().trim().replace(/\s+/g, ' ');
  const normalizedName = normalize(zoneName);
  
  // Use provided t function if available (for reactivity), otherwise use i18n directly
  const translateFn = t || ((key, options) => i18n.t(key, options));
  
  // Get English resources for reverse lookup
  const englishResources = i18n.getResourceBundle('en', 'translation');
  const englishBodyParts = englishResources?.bodyParts || {};
  
  // First, try direct key lookup (zoneName might be a key like 'facies left')
  let translationKey = null;
  if (englishBodyParts[normalizedName]) {
    // The normalized name is a direct key
    translationKey = `bodyParts.${normalizedName}`;
    const translated = translateFn(translationKey, { defaultValue: englishBodyParts[normalizedName] });
    if (translated !== translationKey) {
      return translated;
    }
  }
  
  // Try reverse translation map to get English version first
  const reverseMap = buildReverseTranslationMap();
  let englishZoneName = reverseMap.get(normalizedName);
  
  // If not found in reverse map, try to find by iterating through all languages
  if (!englishZoneName) {
    // Try exact match with original zone name (case-insensitive)
    for (const [key, value] of Object.entries(englishBodyParts)) {
      // Check if the zoneName matches any translated version
      const languages = ['en', 'kn', 'ta', 'ml', 'hi', 'mr', 'te'];
      for (const lang of languages) {
        try {
          const resources = i18n.getResourceBundle(lang, 'translation');
          const langBodyParts = resources?.bodyParts || {};
          const translatedValue = langBodyParts[key];
          if (translatedValue && normalize(translatedValue) === normalizedName) {
            englishZoneName = value;
            break;
          }
        } catch (e) {
          continue;
        }
      }
      if (englishZoneName) break;
    }
  }
  
  // If still not found, try with original zone name (might already be English)
  if (!englishZoneName) {
    // Check if it's already an English body part value
    for (const [key, value] of Object.entries(englishBodyParts)) {
      if (normalize(value) === normalizedName) {
        englishZoneName = value;
        // Also set the key for translation
        translationKey = `bodyParts.${key}`;
        break;
      }
    }
  }
  
  // If still not found, try matching the key directly (case-insensitive)
  if (!englishZoneName) {
    for (const [key, value] of Object.entries(englishBodyParts)) {
      if (normalize(key) === normalizedName) {
        englishZoneName = value;
        translationKey = `bodyParts.${key}`;
        break;
      }
    }
  }
  
  // Fallback to original if we couldn't find English version
  if (!englishZoneName) {
    englishZoneName = zoneName;
  }
  
  const normalizedEnglish = normalize(englishZoneName);
  
  // Now translate the English zone name to current language
  // Try to find the key for this English zone name if we don't have it yet
  if (!translationKey) {
    for (const [key, value] of Object.entries(englishBodyParts)) {
      if (normalize(value) === normalizedEnglish) {
        translationKey = `bodyParts.${key}`;
        break;
      }
    }
  }
  
  // Also try matching by key
  if (!translationKey) {
    for (const [key, value] of Object.entries(englishBodyParts)) {
      if (normalize(key) === normalizedEnglish) {
        translationKey = `bodyParts.${key}`;
        break;
      }
    }
  }
  
  // If we found a key, translate it
  if (translationKey) {
    const translated = translateFn(translationKey, { defaultValue: englishZoneName });
    // If translation worked (not the key itself), return it
    if (translated !== translationKey && translated !== englishZoneName) {
      return translated;
    }
  }
  
  // Last resort: return English version (better than Kannada if we couldn't translate)
  return englishZoneName;
};

/**
 * Always returns the English version of a zone name, regardless of current language
 * This is used in the doctor dashboard to ensure all zone names are displayed in English
 * @param {string} zoneName - The zone name (could be in any language or a key)
 * @returns {string} - English zone name
 */
export const translateZoneNameToEnglish = (zoneName) => {
  if (!zoneName || typeof zoneName !== 'string') {
    return zoneName;
  }

  // Normalize the zone name for comparison (lowercase, trim, remove extra spaces)
  const normalize = (str) => str.toLowerCase().trim().replace(/\s+/g, ' ');
  const normalizedName = normalize(zoneName);
  
  // Get English resources
  const englishResources = i18n.getResourceBundle('en', 'translation');
  const englishBodyParts = englishResources?.bodyParts || {};
  
  // First, try direct lookup - maybe it's already a key or English name
  // Try with normalized name as key
  if (englishBodyParts[normalizedName]) {
    return englishBodyParts[normalizedName];
  }
  
  // Try with original casing/spacing as key
  if (englishBodyParts[zoneName]) {
    return englishBodyParts[zoneName];
  }
  
  // Try with normalized name as translation key format
  const translationKey = `bodyParts.${normalizedName}`;
  const englishTranslation = i18n.t(translationKey, { lng: 'en', defaultValue: null });
  if (englishTranslation && englishTranslation !== translationKey) {
    return englishTranslation;
  }
  
  // Use reverse translation map for efficient lookup
  const reverseMap = buildReverseTranslationMap();
  const englishValue = reverseMap.get(normalizedName);
  if (englishValue) {
    return englishValue;
  }
  
  // Also try with original string (non-normalized)
  const originalNormalized = zoneName.trim().toLowerCase();
  if (reverseMap.has(originalNormalized)) {
    return reverseMap.get(originalNormalized);
  }
  
  // Last resort: try to get English version by forcing English language
  // This handles cases where the zoneName might be a key that works with English
  const currentLang = i18n.language;
  i18n.changeLanguage('en');
  const forcedEnglish = i18n.t(`bodyParts.${normalizedName}`, { defaultValue: zoneName });
  i18n.changeLanguage(currentLang);
  
  // If we got something different from the key, return it
  if (forcedEnglish !== `bodyParts.${normalizedName}` && forcedEnglish !== zoneName) {
    return forcedEnglish;
  }
  
  // Last resort: return the original (might already be in English)
  return zoneName;
};

export default translateZoneName;

